/**
 * Custom touch sensor for @hello-pangea/dnd that tolerates slight finger
 * movement during the long-press phase before cancelling the drag.
 *
 * The default useTouchSensor cancels the drag on any touchmove during the
 * 120 ms pending window. On tablets fingers naturally tremble a few pixels,
 * so the drag is killed before it ever starts.
 *
 * This sensor allows up to MOVEMENT_TOLERANCE_PX of movement before treating
 * the gesture as a scroll. While within tolerance it calls
 * event.preventDefault() to suppress native scrolling.
 */
import { useRef, useLayoutEffect } from "react";
import type { FluidDragActions, PreDragActions, SensorAPI } from "@hello-pangea/dnd";

const LONG_PRESS_DELAY_MS = 120;
const MOVEMENT_TOLERANCE_PX = 8;

type Pos = { x: number; y: number };

type Phase =
	| { type: "IDLE" }
	| { type: "PENDING"; point: Pos; actions: PreDragActions; timerId: ReturnType<typeof setTimeout> }
	| { type: "DRAGGING"; actions: FluidDragActions };

const IDLE: Phase = { type: "IDLE" };

const CAP: AddEventListenerOptions = { capture: true, passive: false };
const CAP_REMOVE_BUBBLE: EventListenerOptions = { capture: false };

function addEvent(name: string, fn: EventListenerOrEventListenerObject, opts: AddEventListenerOptions): () => void {
	window.addEventListener(name, fn, opts);
	return () => window.removeEventListener(name, fn, opts as EventListenerOptions);
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function useTolerantTouchSensor(api: SensorAPI): void {
	const phaseRef = useRef<Phase>(IDLE);
	const unbindRef = useRef<(() => void) | null>(null);

	// eslint-disable-next-line sonarjs/cognitive-complexity
	useLayoutEffect(() => {
		// Use an object so methods can mutually reference each other without
		// circular `const` issues — method bodies are resolved at call time.
		const ctx = {
			stop() {
				const cur = phaseRef.current;
				if (cur.type === "IDLE") return;
				if (cur.type === "PENDING") clearTimeout(cur.timerId);
				phaseRef.current = IDLE;
				unbindRef.current?.();
				unbindRef.current = null;
				ctx.listenForCapture();
			},

			cancel() {
				const cur = phaseRef.current;
				ctx.stop();
				if (cur.type === "DRAGGING") cur.actions.cancel({ shouldBlockNextClick: true });
				if (cur.type === "PENDING") cur.actions.abort();
			},

			startDragging() {
				const cur = phaseRef.current;
				if (cur.type !== "PENDING") return;
				phaseRef.current = { type: "DRAGGING", actions: cur.actions.fluidLift(cur.point) };
			},

			onTouchMove(e: Event) {
				const te = e as TouchEvent;
				const cur = phaseRef.current;

				if (te.touches.length === 0) {
					ctx.cancel();
					return;
				}

				if (cur.type === "PENDING") {
					const dx = Math.abs(te.touches[0].clientX - cur.point.x);
					const dy = Math.abs(te.touches[0].clientY - cur.point.y);
					if (dx <= MOVEMENT_TOLERANCE_PX && dy <= MOVEMENT_TOLERANCE_PX) {
						e.preventDefault(); // block native scroll while waiting for long-press
						return;
					}
					ctx.cancel(); // finger moved too far — treat as scroll
					return;
				}

				if (cur.type !== "DRAGGING") {
					ctx.cancel();
					return;
				}

				e.preventDefault();
				cur.actions.move({ x: te.touches[0].clientX, y: te.touches[0].clientY });
			},

			onTouchEnd(e: Event) {
				const cur = phaseRef.current;
				if (cur.type !== "DRAGGING") {
					ctx.cancel();
					return;
				}
				e.preventDefault();
				cur.actions.drop({ shouldBlockNextClick: true });
				ctx.stop();
			},

			onTouchCancel(e: Event) {
				if (phaseRef.current.type !== "DRAGGING") {
					ctx.cancel();
					return;
				}
				e.preventDefault();
				ctx.cancel();
			},

			bindDragEvents() {
				const unbinders = [
					addEvent("touchmove", ctx.onTouchMove, { capture: false, passive: false }),
					addEvent("touchend", ctx.onTouchEnd, CAP),
					addEvent("touchcancel", ctx.onTouchCancel, CAP),
					addEvent("contextmenu", (e: Event) => e.preventDefault(), CAP),
					addEvent("orientationchange", ctx.cancel as EventListener, CAP),
					addEvent("resize", ctx.cancel as EventListener, CAP),
					addEvent("visibilitychange", ctx.cancel as EventListener, CAP),
					addEvent("keydown", (e: Event) => {
						if ((e as KeyboardEvent).key === "Escape") e.preventDefault();
						ctx.cancel();
					}, CAP),
				];
				unbindRef.current = () => unbinders.forEach(u => u());
			},

			onTouchStart(e: Event) {
				const te = e as TouchEvent;
				if (te.defaultPrevented) return;
				if (te.touches.length === 0) return;

				const draggableId = api.findClosestDraggableId(te);
				if (!draggableId) return;

				const actions = api.tryGetLock(draggableId, ctx.cancel, { sourceEvent: te });
				if (!actions) return;

				const { clientX, clientY } = te.touches[0];

				// Disarm the touchstart listener before binding drag-phase listeners.
				unbindRef.current?.();
				unbindRef.current = null;

				const timerId = setTimeout(() => ctx.startDragging(), LONG_PRESS_DELAY_MS);
				phaseRef.current = { type: "PENDING", point: { x: clientX, y: clientY }, actions, timerId };

				ctx.bindDragEvents();
			},

			listenForCapture() {
				unbindRef.current = addEvent("touchstart", ctx.onTouchStart, CAP);
			},
		};

		ctx.listenForCapture();

		return () => {
			unbindRef.current?.();
			unbindRef.current = null;
			const cur = phaseRef.current;
			if (cur.type === "PENDING") {
				clearTimeout(cur.timerId);
				cur.actions.abort();
				phaseRef.current = IDLE;
			} else if (cur.type === "DRAGGING") {
				cur.actions.cancel({ shouldBlockNextClick: true });
				phaseRef.current = IDLE;
			}
		};
		// api is a stable object provided by DragDropContext — [] is intentional.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Safari/WebKit hack: a non-passive touchmove listener on window is needed
	// so that event.preventDefault() inside dynamically-added handlers actually works.
	// https://github.com/atlassian/react-beautiful-dnd/issues/1374
	useLayoutEffect(() => {
		const noop: EventListener = () => {};
		window.addEventListener("touchmove", noop, { capture: false, passive: false });
		return () => window.removeEventListener("touchmove", noop, CAP_REMOVE_BUBBLE);
	}, []);
}
